import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { environment as ENV } from '../../environments/environment';
import { map } from 'rxjs/operators';
import {
    IStudentCourseEqualization, IStudentCourseEqualizationAssigned, IStudentCourseEqualizationCourse,
    IStudentCourseEqualizationEditModel, IStudentCourseEqualizationListItem, IStudentCourseEqualizationStudent
} from '../models/StudentCourseEqualization';
import { Utils } from '../core/Utils';

export interface ISaveResult {
    Id: number,
    Courses: {
        Id: number,
        Courses: {
            Id: number
        }[]
    }[]
}

/**
 * Student study course equalization service
 */
@Injectable({ providedIn: "root" })
export class StudentCourseEqualizationService {
    constructor(private http: HttpClient) { }

    public get apiUrl(): string { return `${ENV.apiUrl}/studentCourseEqualizations` }

    get(studentEmail?: string): Observable<IStudentCourseEqualizationListItem[]> {
        const url = this.apiUrl;
        const params = this.buildQueryParams({ studentEmail });

        return this.http.get<IStudentCourseEqualizationListItem[]>(url, { params });
    }

    getById(id: number): Observable<IStudentCourseEqualization> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IStudentCourseEqualization>(url);
    }

    getAssigned(): Observable<IStudentCourseEqualizationAssigned[]> {
        const url = `${this.apiUrl}/assigned`;
        return this.http.get<IStudentCourseEqualizationAssigned[]>(url);
    }

    /**
     * Get available courses.
     * @param programId
     * @param studentEmail
     */
    getCourses(programId: string, studentEmail?: string): Observable<IStudentCourseEqualizationCourse[]> {
        const url = `${this.apiUrl}/courses`;
        const params = this.buildQueryParams({
            programId,
            studentEmail
        });

        return this.http.get<IStudentCourseEqualizationCourse[]>(url, { params });
    }

    /**
     * Get available student specialities.
     * @param options
     */
    getStudent(studentEmail?: string): Observable<IStudentCourseEqualizationStudent[]> {
        const url = `${this.apiUrl}/student`;
        const params = this.buildQueryParams({ studentEmail });

        return this.http.get<IStudentCourseEqualizationStudent[]>(url, { params });
    }

    addAttachment(id: number, file: File, outerCourseId?: number): Observable<number> {
        const url = `${this.apiUrl}/${id}/attachments` + (outerCourseId ? `?outerCourseId=${outerCourseId}` : '');
        const form = new FormData();

        form.append('file', file, file.name);

        return this.http.post<number>(url, form);
    }

    removeAttachment(attachmentId: number): Observable<any> {
        const url = `${this.apiUrl}/attachments/${attachmentId}`;
        return this.http.delete(url).pipe(map(res => res));
    }

    getAttachment(attachmentId: number): Observable<any> {
        const url = `${this.apiUrl}/attachments/${attachmentId}`;
        return this.http.post(url, null, { responseType: 'blob' as 'json' });
    }

    create(model: IStudentCourseEqualizationEditModel, studentEmail?: string): Observable<ISaveResult> {
        const url = this.apiUrl;

        const params = this.buildQueryParams({ studentEmail });

        return this.http.post<ISaveResult>(url, model, { params });
    }

    update(id: number, model: IStudentCourseEqualizationEditModel): Observable<ISaveResult> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put<ISaveResult>(url, model);
    }

    delete(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete(url);
    }

    submit(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/submit`;
        return this.http.post(url, null);
    }

    createProject(id: number, decisionId: string, comment?: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/project`;
        return this.http.post(url, { decisionId, comment });
    }

    returnForAddition(id: number, comment?: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/returnForAddition`;
        return this.http.post(url, { comment });
    }

    downloadProtocol(id: number, customTokens?: { [key: string]: any }): Observable<any> {
        const url = `${this.apiUrl}/${id}/protocol`;
        return this.http.post(url, { customTokens }, { responseType: 'blob', observe: 'response' }).pipe(map(response => {
            Utils.downloadResponseFile(response);
            return of(undefined);
        }));
    }

    getDvsProtocolUrl(id: number): string {
        return `${this.apiUrl}/${id}/dvsProtocol`;
    }

    private buildQueryParams(options: any): any {
        if (!options) return {};

        const params: any = {};

        for (const k in options) {
            const val = options[k];

            if (val !== undefined && val !== null) {
                if (typeof val == 'string') {
                    if (val) params[k] = val;
                } else {
                    params[k] = val;
                }
            }
        }

        return params;
    }
}
